import React, { useRef } from "react";
import styled from "styled-components";
import { motion, useInView } from "framer-motion";

const services = [
  {
    num: "01",
    title: "Microneedling",
    subtitle: "Collagen Induction Therapy",
    desc: "Advanced skin rejuvenation using the Dr. Pen A11 Ultima PRO — targeting fine lines, scars, and texture for visibly transformed skin in just a few sessions.",
    tag: "Most Popular",
  },
  {
    num: "02",
    title: "Hydrodermabrasion",
    subtitle: "Deep Cleansing & Hydration",
    desc: "Zemits DermeLuxx Pro technology delivers a multi-step treatment: exfoliation, extraction, and intense hydration in one luxurious session.",
    tag: "Signature",
  },
  {
    num: "03",
    title: "Radiofrequency Lifting",
    subtitle: "Non-Surgical Skin Tightening",
    desc: "Precision RF energy stimulates deep collagen remodeling, lifting and firming facial contours without downtime or discomfort.",
    tag: null,
  },
  {
    num: "04",
    title: "Chemical Peels",
    subtitle: "Targeted Skin Renewal",
    desc: "Customized peel solutions crafted for your skin type — resurfacing the complexion and revealing a luminous, even tone beneath.",
    tag: null,
  },
  {
    num: "05",
    title: "LED Phototherapy",
    subtitle: "Light-Activated Healing",
    desc: "Clinical-grade LED panels deliver targeted wavelengths to calm inflammation, boost collagen, and accelerate cellular repair at a deep level.",
    tag: null,
  },
  {
    num: "06",
    title: "Bespoke Facial",
    subtitle: "Your Skin, Your Protocol",
    desc: "A fully customized treatment designed around your skin's unique needs — combining our most advanced modalities for maximum results.",
    tag: null,
  },
  {
    num: "07",
    title: "Ice Globe Therapy",
    subtitle: "Cryo-Facial Massage",
    desc: "Chilled facial globes glide over the skin to instantly depuff, tighten pores, calm redness, and boost circulation — leaving a refreshed, sculpted glow with zero downtime.",
    tag: null,
  },
  {
    num: "08",
    title: "Oxygen Infusion",
    subtitle: "Pure Radiance Treatment",
    desc: "A pressurized stream of pure oxygen delivers hyaluronic acid and custom serums deep into the skin — plumping, brightening, and reviving even the most fatigued complexions.",
    tag: null,
  },
];

const cardVariants = {
  hidden: { y: 60, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Services = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <Section id="services">
      <Inner>
        <Header>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Label>What We Offer</Label>
          </motion.div>
          <TitleRow>
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <SectionTitle>
                Our
                <br />
                <em>Services</em>
              </SectionTitle>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 1,
                delay: 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <SectionDesc>
                Every treatment at Elaia is a bespoke experience — precision
                protocols engineered for your skin and your results.
              </SectionDesc>
            </motion.div>
          </TitleRow>
        </Header>

        <Grid ref={ref}>
          {services.map((s, i) => (
            <Card
              key={s.num}
              as={motion.div}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              {s.tag && <Tag>{s.tag}</Tag>}
              <Num>{s.num}</Num>
              <CardTitle>{s.title}</CardTitle>
              <CardSub>{s.subtitle}</CardSub>
              <Divider />
              <CardDesc>{s.desc}</CardDesc>
              <CardLink href="#contact">
                Book this treatment <span>→</span>
              </CardLink>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Section>
  );
};

const Section = styled.section`
  background: var(--charcoal);
  padding: 140px 0;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 60px;
    right: 60px;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(196, 149, 122, 0.25),
      transparent
    );
  }
`;
const Inner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 60px;
  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;
const Header = styled.div`
  margin-bottom: 80px;
`;
const Label = styled.span`
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 20px;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  flex-wrap: wrap;
`;
const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(48px, 6vw, 88px);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0;
  em {
    font-style: italic;
    color: var(--rose);
  }
`;
const SectionDesc = styled.p`
  max-width: 380px;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--taupe);
  line-height: 1.8;
  font-weight: 300;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const Card = styled.div`
  position: relative;
  background: rgba(196, 186, 179, 0.4);
  border: 1px solid rgba(196, 149, 122, 0.08);
  padding: 48px 40px;
  cursor: pointer;
  overflow: hidden;
  transition:
    background 0.4s,
    border-color 0.4s;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--rose);
    transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  }
  &:hover::after {
    width: 100%;
  }
  &:hover {
    background: rgba(196, 149, 122, 0.05);
    border-color: rgba(196, 149, 122, 0.2);
  }
  @media (max-width: 768px) {
    padding: 36px 28px;
  }
`;
const Tag = styled.span`
  display: inline-block;
  font-family: var(--font-body);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--charcoal);
  background: var(--rose);
  padding: 4px 12px;
  margin-bottom: 24px;
`;
const Num = styled.span`
  display: block;
  font-family: var(--font-display);
  font-size: 13px;
  color: rgba(196, 149, 122, 0.4);
  letter-spacing: 0.1em;
  margin-bottom: 16px;
`;
const CardTitle = styled.h3`
  font-family: var(--font-display);
  font-size: clamp(24px, 2.5vw, 32px);
  font-weight: 400;
  color: var(--cream);
  letter-spacing: -0.01em;
  margin: 0 0 6px;
`;
const CardSub = styled.p`
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--rose);
  margin: 0;
`;
const Divider = styled.div`
  width: 32px;
  height: 1px;
  background: rgba(196, 149, 122, 0.3);
  margin: 24px 0;
`;
const CardDesc = styled.p`
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 300;
  color: var(--taupe);
  line-height: 1.8;
  margin: 0 0 28px;
`;
const CardLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--cream);
  opacity: 0.5;
  transition:
    opacity 0.3s,
    color 0.3s;
  span {
    transition: transform 0.3s;
  }
  &:hover {
    opacity: 1;
    color: var(--rose);
  }
  &:hover span {
    transform: translateX(6px);
  }
`;

export default Services;
